import {
    closestCenter,
    CollisionDetection,
    defaultDropAnimationSideEffects,
    DndContext,
    DragEndEvent,
    DragOverEvent,
    DragOverlay,
    DropAnimation,
    Modifiers,
    MouseSensor,
    SensorDescriptor,
    TouchSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import {
    rectSortingStrategy,
    SortableContext,
    SortingStrategy,
} from '@dnd-kit/sortable'
import React, { FC, ReactNode, useCallback, useState } from 'react'

type Props = {
    children: ReactNode
    sortableItems: Array<any> // explicitly set as any for reusability
    collisionDetection?: CollisionDetection
    onDragStart?: (event: DragOverEvent) => void
    onDragOver?: (event: DragOverEvent) => void
    onDragCancel?: (event: DragOverEvent) => void
    onDragEnd?: (event: DragEndEvent) => void
    sensors?: Array<SensorDescriptor<any>> // explicitly set as any for reusability
    modifiers?: Modifiers
    strategy?: SortingStrategy
    useDragOverlay?: boolean
    ActiveComponent?: any // explicitly set as any for reusability
    dropAnimation?: DropAnimation | null
}

const dropAnimationConfig: DropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
        styles: {
            active: {
                opacity: '0.5',
            },
        },
    }),
}

const SortableComponent: FC<Props> = ({
    children,
    sortableItems,
    ActiveComponent,
    collisionDetection = closestCenter,
    onDragStart,
    onDragOver,
    onDragEnd,
    onDragCancel,
    sensors,
    modifiers,
    strategy = rectSortingStrategy,
    dropAnimation = dropAnimationConfig,
}) => {
    const mouseSensor = useSensor(MouseSensor, {
        activationConstraint: {
            distance: 25,
        },
    })

    const touchSensor = useSensor(TouchSensor, {
        activationConstraint: {
            distance: 25,
        },
    })

    const sensorArray = useSensors(mouseSensor, touchSensor)

    const [activeId, setActiveId] = useState<string | null>(null)

    const handleDragStart = useCallback(
        (event) => {
            if (!event.active) {
                return
            }

            setActiveId(event.active.id ?? null)

            if (onDragStart) {
                onDragStart(event)
            }
        },
        [onDragStart]
    )

    const handleDragCancel = useCallback(
        (event) => {
            setActiveId(null)
            if (onDragCancel) {
                onDragCancel(event)
            }
        },
        [onDragCancel]
    )

    const handleDragOver = useCallback(
        (event) => {
            if (onDragOver) {
                onDragOver(event)
            }
        },
        [onDragOver]
    )

    const handleDragEnd = useCallback(
        (event) => {
            setActiveId(null)
            if (onDragEnd) {
                onDragEnd(event)
            }
        },
        [onDragEnd]
    )

    return (
        <DndContext
            collisionDetection={collisionDetection}
            onDragStart={handleDragStart}
            onDragCancel={handleDragCancel}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            sensors={sensors ?? sensorArray}
            modifiers={modifiers}
        >
            <SortableContext items={sortableItems} strategy={strategy}>
                {children}
            </SortableContext>
            <DragOverlay dropAnimation={dropAnimation}>
                {activeId !== null ? (
                    <ActiveComponent activeId={activeId} />
                ) : null}
            </DragOverlay>
        </DndContext>
    )
}

export default SortableComponent
